import React, { type ReactNode, useState, useRef, useEffect } from 'react';
import { EngagementSpotlight } from '@atlassiansox/engagekit-ts';
import Timeline from '@atlassiansox/schedule-timeline';
import { format } from 'date-fns-tz';
import Button from '@atlaskit/button';
import Lozenge from '@atlaskit/lozenge';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { Inline, Box, xcss, Text } from '@atlaskit/primitives';
import { SimpleTag as Tag } from '@atlaskit/tag';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { injectIntl, type IntlShape } from '@atlassian/jira-intl';
import type { ScheduleItem, Recipient } from '@atlassian/jira-platform-opsgenie-schedule';
import { getLocale } from '@atlassian/jira-platform-utils-date-fns/src/main.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ScheduleConfigurationConsumer } from '../services/schedule-configuration/index.tsx';
import messages from './messages';

type Props = {
	scheduleItem: ScheduleItem;
	instanceName?: string;
	instanceUrl: string | null;
	teamId: string;
	withSpotlight: boolean;
	teamName?: string;
	onGetBackgroundColor: (recipient: Recipient) => string;
	intl: IntlShape;
};

type EditLinkProps = {
	children: string;
	engagementId: string | undefined;
	instanceUrl: string | null;
	teamId: string;
};

type WithSpotlightProps = {
	engagementId: string;
	children: ReactNode;
};

const WithSpotlight = ({ children, engagementId }: WithSpotlightProps) => (
	<>
		<SpotlightTarget name={engagementId}>{children}</SpotlightTarget>
		<EngagementSpotlight engagementId={engagementId} />
	</>
);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const onEditLinkClicked = (_: any, analyticsEvent: any) => {
	fireUIAnalytics(analyticsEvent, 'link clicked', 'editScheduleLink');
};

const EditLink = ({ children, engagementId, instanceUrl, teamId }: EditLinkProps) => {
	const props = {
		appearance: 'link',
		href: `${instanceUrl || ''}/teams/dashboard/${teamId}/main`,
		isDisabled: !instanceUrl,
		target: '_blank',
		onClick: onEditLinkClicked,
	};

	return engagementId != null ? (
		<WithSpotlight engagementId={engagementId}>
			{/* @ts-expect-error - TS2322 - Type '{ children: string; appearance: string; href: string; isDisabled: boolean; target: string; onClick: (_: any, analyticsEvent: any) => void; }' is not assignable to type 'ButtonProps'. */}
			<Button {...props}>{children}</Button>
		</WithSpotlight>
	) : (
		// @ts-expect-error - TS2322 - Type '{ children: string; appearance: string; href: string; isDisabled: boolean; target: string; onClick: (_: any, analyticsEvent: any) => void; }' is not assignable to type 'ButtonProps'.
		<Button {...props}>{children}</Button>
	);
};

EditLink.defaultProps = { engagementId: undefined };

const RENDER_WAIT_MS = 500;

const InnerScheduleTimeline = ({
	scheduleItem: {
		onCallRecipients,
		finalTimeline,
		startDate,
		schedule: { timezone, name: scheduleName, enabled },
	},

	instanceUrl,
	onGetBackgroundColor,
	withSpotlight,
	intl,
	teamId,
	teamName,
}: Props) => {
	const { formatMessage } = intl;
	const [tooltipText, setTooltipText] = useState('');
	const teamNameRef = useRef<HTMLSpanElement>(null);

	useEffect(() => {
		// check if tooltip is required after 500ms, giving enough time to let the elements rendered in DOM
		const useEffectTimer = setTimeout(() => {
			if (teamNameRef.current) {
				const nameVisibleWidth = teamNameRef.current.offsetWidth;
				const nameFullWidth = teamNameRef.current.scrollWidth;
				if (nameFullWidth > nameVisibleWidth && teamName !== undefined) {
					// team name is truncated, render tooltip
					setTooltipText(teamName);
				}
			}
		}, RENDER_WAIT_MS);

		return () => clearTimeout(useEffectTimer);
	});

	return (
		<ScheduleConfigurationConsumer>
			{({ interval, intervalUnit, dateFormat, currentDate }) => (
				<Box
					// eslint-disable-next-line jira/integration/enforce-data-testid-usage
					data-test-id="platform-opsgenie-schedule-timeline.ui.timeline"
					xcss={schedulContainerStyles}
				>
					<Inline xcss={schedulerHeaderStyles}>
						<Inline xcss={scheduleInfoStyles}>
							<Box xcss={scheduleTitleStyles}>{scheduleName}</Box>
							<Inline xcss={scheduleTeamNameAndTimezoneWrapperStyles}>
								{teamName != null && (
									<>
										<Tooltip content={tooltipText}>
											<Inline as="span" xcss={scheduleTeamNameStyles} ref={teamNameRef}>
												{teamName}
											</Inline>
										</Tooltip>
										{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
										<span>&nbsp;&middot;&nbsp;</span>
									</>
								)}
								<Box as="span" xcss={scheduleNameAndTimeZoneSeparatorStyles} />
								<Text as="span" size="small">
									{`${format(new Date(), '(XXX) z', {
										timeZone: timezone,
										locale: getLocale(Intl.DateTimeFormat().resolvedOptions().locale),
									})} ${timezone}`}
								</Text>
							</Inline>
							<Box xcss={scheduleStatusStyles}>
								{enabled ? (
									<Lozenge appearance="success">{formatMessage(messages.enabled)}</Lozenge>
								) : (
									<Lozenge>{formatMessage(messages.disabled)}</Lozenge>
								)}
							</Box>
							<Box xcss={recipientsContainerStyles}>
								{onCallRecipients.map((recipient) => (
									<Tag
										key={recipient.id}
										appearance="rounded"
										text={recipient.name != null ? recipient.name : 'unknown'}
									/>
								))}
							</Box>
						</Inline>

						<EditLink
							engagementId={withSpotlight ? 'edit-schedules-button' : undefined}
							{...{ instanceUrl, teamId }}
						>
							{formatMessage(messages.viewInOpsgenie)}
						</EditLink>
					</Inline>
					<Timeline
						{...{
							startDate,
							timezone,
							interval,
							intervalUnit,
							dateFormat,
							currentDate,
						}}
						locale={intl.locale}
						// @ts-expect-error - TS2322 - Type '{ name: string; periods: { textColor?: string | undefined; startDate: string; endDate: string; type: string; label: string | undefined; backgroundColor: string; }[]; }[]' is not assignable to type 'Rotation[]'.
						rotations={finalTimeline.rotations.map(({ name, periods }) => ({
							name,
							periods: periods.map((period) => ({
								startDate: period.startDate,
								endDate: period.endDate,
								type: period.type,
								label: period.recipient.name !== null ? period.recipient.name : '--',
								backgroundColor: onGetBackgroundColor(period.recipient),
								...(period.recipient.name === '--' && {
									textColor: token('color.text', colors.N900),
								}),
							})),
						}))}
					/>
				</Box>
			)}
		</ScheduleConfigurationConsumer>
	);
};

function ScheduleTimeline(props: Props) {
	const { withSpotlight } = props;

	if (withSpotlight) {
		return (
			<WithSpotlight engagementId="opsgenie-schedule">
				<InnerScheduleTimeline {...props} />
			</WithSpotlight>
		);
	}

	return <InnerScheduleTimeline {...props} />;
}

export default injectIntl(ScheduleTimeline);

const schedulContainerStyles = xcss({
	borderRadius: 'space.025',
	backgroundColor: 'elevation.surface.raised',
	boxShadow: 'elevation.shadow.raised',
	padding: 'space.200',
	marginBottom: 'space.300',
	overflow: 'auto',
	minWidth: '500px',
});

const scheduleTitleStyles = xcss({
	color: 'color.text',
	fontSize: 'font.body',
	fontWeight: 600,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	maxWidth: '248px',
});

const schedulerHeaderStyles = xcss({
	justifyContent: 'space-between',
	alignItems: 'center',
	paddingBottom: 'space.100',
});

const scheduleInfoStyles = xcss({
	alignItems: 'center',
	flexWrap: 'wrap',
	minWidth: '500px',
	width: '100%',
});

const scheduleTeamNameAndTimezoneWrapperStyles = xcss({
	marginLeft: 'space.100',
	color: 'color.text.subtle',
	verticalAlign: 'middle',
	whiteSpace: 'nowrap',
});

const scheduleTeamNameStyles = xcss({
	maxWidth: '248px',
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	display: 'inline-block',
});

const scheduleNameAndTimeZoneSeparatorStyles = xcss({
	content: "'\u00A0\u00B7\u00A0'",
});

const scheduleStatusStyles = xcss({
	marginTop: 'space.0',
	marginBottom: 'space.0',
	marginRight: 'space.100',
	marginLeft: 'space.100',
	display: 'inherit',
});

const recipientsContainerStyles = xcss({
	minWidth: '200px',
	marginLeft: 'space.negative.050',
});
